/* Atlas v3 — shared atoms / helpers / contexts.
   ES-module port of the mockup's window-global primitives
   (atlas-home.jsx + atlas-core.jsx). The design-system foundation
   that every v3 screen reuses. Tokens live in src/v2/v3.css — do NOT
   redefine them here.

   Design rules enforced here: true-black base, amber as line/label
   only, green/red for direction only, three type roles (Instrument
   Serif display / JetBrains Mono numbers / system sans body). */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react'

/* ── prefers-reduced-motion ─────────────────────────────── */
export const REDUCE =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/* read an hsl token from a CSS var, with optional alpha — for canvas drawing */
export function tok(name, a = 1) {
  if (typeof document === 'undefined') return `hsl(0 0% 0% / ${a})`
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return `hsl(${v} / ${a})`
}

/* ── contexts ───────────────────────────────────────────── */
// TickerCtx carries openTicker(symbol) — wired to the real StockDetail
// drawer by the Overview shell. CfgCtx carries the small live-config the
// mockup's Tweaks panel drove; in-app we just use the defaults.
export const DEFAULT_CFG = {
  flavor: 'punchy',
  accent: 'amber',
  theme: 'ink',
  heroSize: 40,
  pulseStrip: true,
}
export const CfgCtx = createContext(DEFAULT_CFG)
export const useCfg = () => useContext(CfgCtx)
export const TickerCtx = createContext(() => {})
export const useTicker = () => useContext(TickerCtx)

/* ── market hours ───────────────────────────────────────── */
export function marketState() {
  try {
    const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }))
    const day = et.getDay(), h = et.getHours(), m = et.getMinutes(), mins = h * 60 + m
    const fmtRem = (r) => (r >= 60 ? `${Math.floor(r / 60)}h ${String(r % 60).padStart(2, '0')}m` : `${r}m`)
    if (day === 0 || day === 6) return { dot: '--studio-danger', label: 'Markets closed · Opens Mon 9:30 AM ET', open: false }
    if (mins < 240) return { dot: '--studio-danger', label: 'Market closed · Opens 9:30 AM ET', open: false }
    if (mins < 570) { const r = 570 - mins; return { dot: '--studio-accent', label: `Pre-market · Opens in ${fmtRem(r)}`, open: false } }
    if (mins < 960) { const r = 960 - mins; return { dot: '--studio-success', label: `Market open · Closes in ${fmtRem(r)}`, open: true } }
    if (mins < 1200) return { dot: '--studio-accent', label: 'After hours · Extended to 8:00 PM ET', open: false }
    return { dot: '--studio-danger', label: 'Market closed · Opens 9:30 AM ET', open: false }
  } catch {
    return { dot: '--studio-text-subtle', label: 'Market data loading...', open: false }
  }
}

export function intent(up) { return up ? '--studio-success' : '--studio-danger' }

/* ── small ui atoms ─────────────────────────────────────── */
const KICK = {
  fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.18em',
  textTransform: 'uppercase', color: 'hsl(var(--studio-accent))',
}
export function Kicker({ children, color }) {
  return <div style={{ ...KICK, color: color || KICK.color }}>{children}</div>
}
export function Section({ children, style }) {
  return <section style={{ padding: '0 20px', marginBottom: 30, ...style }}>{children}</section>
}
export function SecHead({ kicker, kickerColor, action, onAction }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 13 }}>
      <Kicker color={kickerColor}>{kicker}</Kicker>
      {action && (onAction
        ? <button onClick={onAction} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--studio-font-mono)', fontSize: 10.5, color: 'hsl(var(--studio-accent-soft))', letterSpacing: '0.04em' }}>{action}</button>
        : <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))', letterSpacing: '0.04em' }}>{action}</span>)}
    </div>
  )
}
export function Chip({ active, children, onClick, disabled }) {
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled} style={{
      fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.1em', padding: '5px 6px',
      cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap', transition: 'all 150ms var(--studio-ease)',
      background: 'transparent', border: 'none',
      borderBottom: active ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid transparent',
      color: disabled ? 'hsl(var(--studio-text-subtle))' : active ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-muted))',
      opacity: disabled ? 0.4 : 1,
    }}>{children}</button>
  )
}

/* ── Dropdown — compact inline header control ("LABEL ▾" trigger + popover
   list). The desks' timeframe/sector-filter idiom (LeverageDesk/GlobalDesk),
   hoisted here so new screens stop copy-pasting it. The desks keep their
   local copies until their next edit swaps them for this export. ── */
export function Dropdown({ value, options, onChange, align = 'left', size = 11, fallbackLabel, minWidth = 150, maxHeight = 300 }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('pointerdown', h)
    return () => document.removeEventListener('pointerdown', h)
  }, [open])
  const cur = options.find((o) => o.value === value)
  const isDefault = value === 'all'
  const label = (isDefault && fallbackLabel) ? fallbackLabel : (cur?.label ?? value)
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex', minWidth: 0, maxWidth: '100%' }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        fontFamily: 'var(--studio-font-mono)', fontSize: size, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer',
        background: 'none', border: 'none', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%',
        color: (open || !isDefault) ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-subtle))',
      }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ fontSize: size - 2, flexShrink: 0, color: 'hsl(var(--studio-accent) / 0.8)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 160ms' }}>▾</span>
      </button>
      {open && (
        <div className="atlas-scroll" style={{
          position: 'absolute', top: 'calc(100% + 7px)', [align]: 0, zIndex: 40, minWidth, maxHeight, overflowY: 'auto',
          background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius)',
          boxShadow: '0 14px 36px -10px hsl(var(--studio-overlay))',
        }}>
          {options.map((o) => (
            <button key={o.value} onClick={() => { onChange(o.value); setOpen(false) }} style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', fontFamily: 'var(--studio-font-mono)', fontSize: 11, letterSpacing: '0.06em',
              textTransform: 'uppercase', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
              background: o.value === value ? 'hsl(var(--studio-accent) / 0.12)' : 'transparent',
              color: o.value === value ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-muted))',
            }}>{o.label}</button>
          ))}
        </div>
      )}
    </span>
  )
}

/* ── skeleton placeholders ──────────────────────────────── */
export function Skel({ w, h, r = 4 }) {
  return <div className="atlas-skeleton" style={{ width: w, height: h, borderRadius: r, flexShrink: 0 }} />
}
export function SkeletonRow({ i }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 20px', borderTop: i ? '1px solid hsl(var(--studio-border) / 0.35)' : 'none' }}>
      <Skel w={18} h={10} /><Skel w={38} h={12} />
      <div style={{ flex: 1 }}><Skel w="80%" h={10} /></div>
      <Skel w={36} h={18} /><Skel w={56} h={28} />
    </div>
  )
}
/* ── CountUp — tween a number toward its target. Animates from 0 on first
   mount (the editorial flourish), then from the PREVIOUS value on later
   changes so live/scrub updates glide instead of snapping back to 0.
   `enabled=false` returns the target instantly with no tween — used by the
   hero headline while scrubbing the chart, where the number must track the
   finger frame-for-frame (a 900ms count-up restarting on every crosshair
   move was making the readout lag and sit near 0.0). ──── */
export function useCountUp(target, ms = 900, deps = [], enabled = true) {
  const [v, setV] = useState(REDUCE || !enabled ? target : 0)
  const fromRef = useRef(REDUCE || !enabled ? target : 0)
  useEffect(() => {
    if (REDUCE || !enabled) { setV(target); fromRef.current = target; return }
    let raf, start
    const from = fromRef.current
    const step = (t) => {
      if (!start) start = t
      const p = Math.min(1, (t - start) / ms)
      const e = 1 - Math.pow(1 - p, 3) // easeOutCubic
      setV(from + (target - from) * e)
      if (p < 1) raf = requestAnimationFrame(step)
      else fromRef.current = target
    }
    raf = requestAnimationFrame(step)
    const fallback = setTimeout(() => { setV(target); fromRef.current = target }, ms + 200)
    return () => { cancelAnimationFrame(raf); clearTimeout(fallback) }
  }, deps) // eslint-disable-line react-hooks/exhaustive-deps
  return enabled ? v : target
}

export function Pct({ value, digits = 2, sign = true, animate = true, style }) {
  const v = useCountUp(value, 900, [value], animate)
  const s = `${sign && value > 0 ? '+' : ''}${v.toFixed(digits)}%`
  return <span style={{ fontVariantNumeric: 'tabular-nums', ...style }}>{s}</span>
}

/* ── Spark — tiny SVG sparkline (port of the mockup's AnimatedSpark) ──
   Directional: green up / red down. Draws on mount unless reduced-motion.
   Shared by MyStocks rows + Explore rows/carousel. */
let SPK_SEQ = 0
export function Spark({ data, up, width = 64, height = 26 }) {
  const ref = useRef(null)
  const [len, setLen] = useState(0)
  const [aid] = useState(() => ++SPK_SEQ)
  // No real series → flat, muted line so the spark reads as "no data,"
  // not a fake green rally (the price snapshot carries no spark array yet).
  const hasData = data && data.length > 1
  const color = hasData ? (up ? '--studio-success' : '--studio-danger') : '--studio-text-subtle'
  const series = hasData ? data : [5, 5, 5, 5, 5, 5, 5, 5]
  const min = Math.min(...series), max = Math.max(...series)
  const pad = (max - min) * 0.18 || 1
  const lo = min - pad, hi = max + pad
  const pts = series.map((d, i) => [
    (i / (series.length - 1)) * width,
    height - ((d - lo) / (hi - lo)) * height,
  ])
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')
  useEffect(() => { if (ref.current) setLen(ref.current.getTotalLength()) }, [line])
  const animName = 'spkDraw' + aid
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ flexShrink: 0, display: 'block', overflow: 'visible' }}>
      <path
        ref={ref}
        d={line}
        fill="none"
        stroke={tok(color, 0.9)}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        style={{
          strokeDasharray: len || undefined,
          strokeDashoffset: 0,
          // Draw only REAL data. Rows mount before sparks load — animating the
          // flat placeholder means every spark draws twice (placeholder draw,
          // then a replay when the series lands and the keyframes recompute).
          animation: (hasData && len && !REDUCE) ? `${animName} 700ms var(--studio-ease)` : 'none',
        }}
      />
      {pts.length > 0 && (
        <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.2" fill={tok(color)} />
      )}
      {len > 0 && <style>{`@keyframes ${animName}{from{stroke-dashoffset:${len}px}to{stroke-dashoffset:0}}`}</style>}
    </svg>
  )
}

/* ── SignalBadge — signal_type → label + color (matches real DB keys) ── */
export const SIGNAL_MAP = {
  options_unusual: { tok: '--studio-accent', label: 'OPTIONS' },
  '8k_filing': { tok: '--studio-accent', label: '8-K' },
  insider_trade: { tok: '--studio-success', label: 'INSIDER' },
  nvda_cascade: { tok: '--studio-warning', label: 'CASCADE' },
  earnings: { tok: '--studio-danger', label: 'EARNINGS' },
  momentum: { tok: '--studio-success', label: 'MOMENTUM' },
  ceo_cfo_transition: { tok: '--studio-danger', label: 'EXEC' },
  secondary_offering: { tok: '--studio-text-subtle', label: 'OFFERING' },
  datacenter_power: { tok: '--studio-accent', label: 'POWER' },
  news_sentiment_shift: { tok: '--studio-accent', label: 'BUZZ' },
  news: { tok: '--studio-accent', label: 'NEWS' },
}
export function SignalBadge({ type }) {
  const cfg = SIGNAL_MAP[type] || { tok: '--studio-text-subtle', label: 'SIGNAL' }
  return (
    <span style={{
      fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.1em',
      padding: '3px 7px', borderRadius: 'var(--studio-radius-sm)',
      background: `hsl(var(${cfg.tok}) / 0.14)`,
      border: `1px solid hsl(var(${cfg.tok}) / 0.35)`,
      color: `hsl(var(${cfg.tok}))`,
    }}>{cfg.label}</span>
  )
}

/* Map a Wire feed-item (signal) into the NewsSheet reader shape so tapping a
   signal opens the same in-app writeup window as a news card. The signal's
   write-up becomes the headline, the type becomes the small source label, and
   any detail blurb becomes the body. News items pass through unchanged. */
export function signalToSheet(item) {
  if (!item || item.kind !== 'signal') return item
  const label = (SIGNAL_MAP[item.type] || {}).label || 'SIGNAL'
  return { ...item, why: label, summary: item.summary || item.why || null }
}

/* ── 8-K item codes → plain English. Raw SEC item numbers (2.03, 5.02, …) are
   meaningless in a headline; the marquee should say what happened, not cite the
   form. 9.01 (exhibits) is filler — mapped to null so it's dropped when a real
   item is present. Codes not here fall back to a generic "Filed an 8-K." */
const EIGHTK_ITEMS = {
  '1.01': 'a new material agreement',
  '1.02': 'the end of a material agreement',
  '1.03': 'bankruptcy or receivership',
  '2.01': 'an acquisition or asset sale',
  '2.02': 'quarterly results',
  '2.03': 'a new debt obligation',
  '2.04': 'an accelerated debt obligation',
  '2.05': 'restructuring costs',
  '2.06': 'a material impairment',
  '3.01': 'a delisting notice',
  '3.02': 'an unregistered equity sale',
  '3.03': 'a change to shareholder rights',
  '4.01': 'an auditor change',
  '4.02': 'a financial restatement',
  '5.01': 'a change in control',
  '5.02': 'an executive change',
  '5.03': 'a charter or bylaw change',
  '5.07': 'a shareholder vote',
  '7.01': 'a Reg FD disclosure',
  '8.01': 'a corporate update',
  '9.01': null, // exhibits — filler, dropped when a real item is present
}
/* join phrases with an Oxford-comma "and" — one/two/many */
function joinPhrases(arr) {
  if (arr.length === 1) return arr[0]
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`
  return `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`
}

/* ── signalWriteup — a readable sentence per signal, composed from its real
   `detail` fields (the raw signal_events headlines are terse auto-strings like
   "1 unusual contracts (call/put ratio 2.52)"). Falls back to the stored
   headline, then a generic label. Used by the Overview "On the Wire" carousel
   + the Wire feed so signals read as prose, not data fragments. No fabrication
   — only rephrasing of fields that are already there. */
export function signalWriteup(sig) {
  if (!sig) return ''
  const d = sig.details || sig.detail || {}
  const t = sig.ticker || ''
  const num = (v) => (typeof v === 'number' ? v : Number(v))
  const money = (v) => {
    const n = Math.abs(num(v))
    if (!isFinite(n) || n === 0) return null
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`
    return `$${n.toFixed(0)}`
  }
  switch (sig.signal_type) {
    case 'news_sentiment_shift': {
      const pct = num(d.bullish_pct)
      const n = num(d.articles_last_week) || num(d.articles_with_insights)
      if (isFinite(pct) && isFinite(n)) {
        const lead = pct >= 60 ? 'Buzz building' : pct <= 40 ? 'Sentiment cooling' : 'News mixed'
        return `${lead} — ${Math.round(pct)}% bullish across ${n} article${n === 1 ? '' : 's'} this week.`
      }
      break
    }
    case 'options_unusual': {
      const r = num(d.call_put_ratio)
      const n = num(d.unusual_count)
      const prem = money(d.total_premium) // null for pre-2026-07 rows without premium
      const chainVol = num(d.chain_volume)
      // Display contract: a printed ratio is BACKED by >= 500 chain contracts —
      // the same floor the scorer uses before trusting the ratio for direction.
      // Thin chains show premium-led copy with no fake-precision multiplier.
      // Legacy rows (no chain_volume field) keep their ratio rather than blank.
      const ratioOk = isFinite(r) && (!isFinite(chainVol) || chainVol >= 500)
      const cnt = isFinite(n) && n > 0 ? ` across ${n} flagged contract${n === 1 ? '' : 's'}` : ''
      if (ratioOk) {
        const dir = r >= 1 ? 'call-heavy' : 'put-heavy'
        const lead = prem ? `~${prem} in premium, ` : ''
        return `Unusual options flow — ${lead}${r.toFixed(1)}× call/put (${dir})${cnt}.`
      }
      if (prem) return `Unusual options flow — ~${prem} in premium${cnt} (thin chain).`
      if (isFinite(n) && n > 0) return `Unusual options flow — ${n} flagged contract${n === 1 ? '' : 's'} (thin chain).`
      break
    }
    case 'insider_trade': {
      const sold = d.code === 'S' || num(d.change) < 0
      const sh = Math.abs(num(d.change))
      const amt = isFinite(num(d.price)) && isFinite(sh) && sh > 0 ? money(num(d.price) * sh) : money(d.value)
      if (amt) return `Insider ${sold ? 'sold' : 'bought'} ${amt}${d.planned ? ' (planned)' : ''}.`
      break
    }
    case '8k_filing': {
      const items = Array.isArray(d.items) ? d.items : null
      if (items && items.length) {
        // Map raw item codes → plain English, drop filler (9.01) + unknowns.
        const phrases = [...new Set(items.map((it) => EIGHTK_ITEMS[String(it).trim()]).filter(Boolean))]
        if (phrases.length) return `Filed an 8-K disclosing ${joinPhrases(phrases)}.`
        return 'Filed an 8-K.' // items present but all filler/unmapped → keep codes out of the headline
      }
      break
    }
    case 'ceo_cfo_transition':
      return 'Leadership change — an officer/director transition was disclosed (8-K Item 5.02).'
    case 'earnings': {
      const a = num(d.eps_actual); const e = num(d.eps_estimate)
      if (isFinite(a) && isFinite(e) && e !== 0) {
        const surprise = ((a - e) / Math.abs(e)) * 100
        const verb = surprise >= 0.5 ? 'beat' : surprise <= -0.5 ? 'missed' : 'met'
        return `Reported earnings — EPS ${a} vs ${e} est, ${verb} by ${Math.abs(surprise).toFixed(1)}%.`
      }
      break
    }
    case 'secondary_offering': {
      const pct = num(d.pct_estimate)
      if (isFinite(pct)) return `Secondary offering filed — ~${pct}% potential dilution.`
      break
    }
    case 'datacenter_power':
      if (d.capex_status) return `Datacenter-power read — AI capex cycle ${String(d.capex_status).toLowerCase()}.`
      break
    default:
      break
  }
  return sig.headline || d.summary || `${(SIGNAL_MAP[sig.signal_type] || {}).label || 'Signal'} on ${t}`
}